import { useState } from "react";
import ArrowRight from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ArrowRight24PxOutlined";
import BuyNowPayLater48PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/BuyNowPayLater48PxOutlined";
import styles from "./CreditOffer.module.scss";
import { Icon } from "./components/Icon/Icon";
import { ListItem } from "../__shared__/ListItem/ListItem";
import { CreditIllustration, ValueAndDisplay } from "../../types/cobeTypes";
import { CreditOffersSideDrawer } from "./components/CreditOfferSideDrawer/CreditOffersSideDrawer";

export type CreditOfferProps = {
  /**
   * An array of ProductVariantEligibilityV2 objects representing the credit offers.
   */
  offers: CreditIllustration[];
  /**
   * A function that handles item click events, taking a string subtype as an argument.
   */
  ifcDisclaimerAnalyticsClickHandler: (subtype: string) => void;
  /**
   * A function that handles IBC item analytics events, taking a type and a subtype as arguments.
   */
  ibcDisclaimerAnalyticsClickHandler: (type: string, subtype: string) => void;
  /**
   * A boolean that specifies whether or not to hide the component if it only has IBC offers
   */
  shouldHideIfIBCOnly?: boolean;
};

export function CreditOfferUI({
  offers,
  ifcDisclaimerAnalyticsClickHandler,
  ibcDisclaimerAnalyticsClickHandler,
  shouldHideIfIBCOnly,
}: CreditOfferProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const hasJLOffers = offers?.some((obj) => obj.creditProvider === "BNPP");

  if (offers.length === 0 || !hasJLOffers) {
    return null;
  }

  const hasIBCOffersOnly = offers?.every(
    (obj) => obj.financialProductType === "IBC",
  );

  if (typeof shouldHideIfIBCOnly === "undefined") {
    return null;
  }

  if (shouldHideIfIBCOnly && hasIBCOffersOnly) return null;
  if (!shouldHideIfIBCOnly && !hasIBCOffersOnly) return null;

  if (!offers.length) {
    return null;
  }
  const generateLabelText = (
    firstOffer: CreditIllustration,
    offersLength: number,
  ) => {
    const {
      financialProductType,
      repaymentAmount: { display: monthlyRepaymentDisplay },
      creditTerm: { display: creditTermDisplay },
      creditRate: { display: creditRateDisplay },
      holidayPeriod,
    } = firstOffer;

    const isIFC = financialProductType === "IFC";
    return (
      <>
        {(financialProductType === "IBC" || financialProductType === "IFC") && (
          <span>
            Pay {monthlyRepaymentDisplay} per month {isIFC && "interest free"}{" "}
            over {creditTermDisplay}
            {!isIFC && ` at ${creditRateDisplay} APR`}
          </span>
        )}
        {financialProductType === "DEFERRED_IBC" && (
          <span>
            Pay your balance in full within {holidayPeriod?.display} and pay no
            interest
          </span>
        )}
        {offersLength > 1 ? ". More payment plans available" : undefined}
      </>
    );
  };
  const [firstOffer] = offers;

  const isIFC = firstOffer?.financialProductType === "IFC";

  const handleOpenDrawer = () => {
    // dispatchMonetateQEvent("standard-pdp-credit-offer-open-drawer");

    if (isIFC && ifcDisclaimerAnalyticsClickHandler) {
      ifcDisclaimerAnalyticsClickHandler("credit component");
    }

    if (!isIFC && ibcDisclaimerAnalyticsClickHandler) {
      ibcDisclaimerAnalyticsClickHandler("fs slide draw", "pdp");
    }

    setIsDrawerOpen(true);
  };

  const icon =
    firstOffer?.financialProductType === "DEFERRED_IBC" ? (
      <BuyNowPayLater48PxOutlinedIcon />
    ) : (
      <Icon creditRate={firstOffer?.creditRate as ValueAndDisplay} />
    );

  const labelText = generateLabelText(
    firstOffer as CreditIllustration,
    offers.length,
  );

  return (
    <>
      <ListItem
        label={labelText}
        trailingIcon={<ArrowRight />}
        showBottomBorder
        showTopBorder
        onClick={handleOpenDrawer}
        className={styles.message}
        leadingIcon={icon}
        dataComponent="CreditOffer"
      />
      <CreditOffersSideDrawer
        handleClose={() => setIsDrawerOpen(false)}
        drawerIsOpen={isDrawerOpen}
        creditOffers={offers}
        ifcDisclaimerAnalyticsClickHandler={ifcDisclaimerAnalyticsClickHandler}
        ibcDisclaimerAnalyticsClickHandler={ibcDisclaimerAnalyticsClickHandler}
      />
    </>
  );
}
